/**
 * StageAwareToolStrip
 *
 * Slim persistent bottom strip that follows the user across public
 * pages and surfaces 2 contextual workspace tools based on:
 *   1. An explicit stage saved in localStorage (`bp_stage`)
 *   2. The current URL pattern (heuristic stage + topic detection)
 *
 * Hidden on workspace/admin/auth/checkout pages and remembers when the
 * user dismisses it (session-scoped, comes back next visit).
 *
 * Stages:
 *   - researching  (guides / blog / suburbs)
 *   - quoting      (builder profiles / find a builder / quote pages)
 *   - building     (active build, contracts, inspections)
 *   - defects      (post-handover, complaints)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, ArrowUpRight } from 'lucide-react';
import { TOOL_REGISTRY } from './WorkspaceToolGrid';
import useAffiliateProfileActive from '../hooks/useAffiliateProfileActive';

const SESSION_DISMISS_KEY = 'bp_stage_strip_dismissed';
const STAGE_KEY = 'bp_stage';

// Hide on these path prefixes - either the user is already inside the
// workspace, mid-checkout, or in an admin/auth surface where another
// floating bar would be noise.
const HIDDEN_PREFIXES = [
  '/workspace',
  '/admin',
  '/auth',
  '/login',
  '/signup',
  '/checkout',
  '/billing',
  '/welcome',
  '/page-not-found',
  '/start', // paid Meta funnel LPs - keep them single-action, no leaky nav
];

// Early-stage building content ONLY. The strip is a planning aid, so it must
// appear exclusively on pages about the early phase of a build (deciding to
// build, researching designs/land/costs, and choosing/comparing a builder) —
// across every building type. Anything not matched here shows NO strip
// (no catch-all), so it never appears on products, styling, guides, etc.
const STAGE_RULES = [
  // Choosing / comparing a builder (still early planning)
  { stage: 'quoting', tools: ['quote-comparison', 'builder-licence-check'],
    matches: ['/builders/', '/home-builders-in', '/find/', '/find-a-builder', '/best-builder', '/best-new-home-builders', '/best-home-builders', '/quote', '/compare-builders'] },
  // Build costs / budgeting a build
  { stage: 'quoting', tools: ['quote-comparison', 'cost-tracker'],
    matches: ['/building-costs-adelaide', '/building-costs', '/hidden-costs', '/site-costs', '/build-cost', '/cost-of-building'] },
  // Researching / planning a build (all building types)
  { stage: 'researching', tools: ['lifestyle-discovery', 'house-and-land'],
    matches: [
      '/building-a-new-home', '/knockdown-rebuild', '/knockdown', '/house-and-land',
      '/first-home-buyer', '/first-home', '/choosing-land', '/land-for-sale', '/land',
      '/display-home', '/suburbs', '/best-suburbs-to-build', '/custom-home-builder',
      '/sloping-block', '/two-storey', '/property-development', '/home-design',
    ] },
  { stage: 'researching', tools: ['furniture-planner', 'facade-visualiser'],
    matches: ['/home-designs/'] },
];

const STAGE_LABEL = {
  researching: 'Researching',
  quoting:     'Comparing quotes',
};

// Returns the matching early-stage rule, or null if this page is not early-
// stage building content (in which case the strip does not render).
const detectFromPath = (pathname) => {
  for (const rule of STAGE_RULES) {
    if (rule.matches.some((m) => pathname.includes(m))) return rule;
  }
  return null;
};

const StageAwareToolStrip = () => {
  const { pathname } = useLocation();
  const affiliateActive = useAffiliateProfileActive();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(SESSION_DISMISS_KEY) === '1'; } catch { return false; }
  });
  // Show after a small delay so it doesn't flash during nav transitions
  // and doesn't compete with the hero on first paint.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    // Hide immediately on path change, then re-show after the delay.
    let active = true;
    const t = setTimeout(() => { if (active) setVisible(true); }, 1200);
    return () => { active = false; clearTimeout(t); setVisible(false); };
  }, [pathname]);

  const hideOnThisPath = HIDDEN_PREFIXES.some((p) => pathname.startsWith(p));

  // Stage override from localStorage takes precedence (e.g. user later
  // explicitly sets their stage via a profile setting).
  const explicit = useMemo(() => {
    try { return localStorage.getItem(STAGE_KEY); } catch { return null; }
  }, [pathname]);

  const detection = detectFromPath(pathname);
  const effectiveStage = detection && explicit && STAGE_LABEL[explicit] ? explicit : (detection ? detection.stage : null);
  const tools = detection ? detection.tools.slice(0, 2) : [];

  if (!detection) return null; // only early-stage building content
  if (dismissed) return null;
  if (affiliateActive) return null; // off-message on affiliate shopping pages
  if (hideOnThisPath) return null;
  if (pathname === '/') return null; // homepage already has a dense CTA stack
  if (!visible) return null;

  const onDismiss = () => {
    try { sessionStorage.setItem(SESSION_DISMISS_KEY, '1'); } catch { /* noop */ }
    setDismissed(true);
  };

  return (
    <div
      data-testid="stage-aware-strip"
      role="region"
      aria-label="Recommended tools"
      className="bp-stage-strip"
    >
      <div className="bp-stage-strip-inner">
        <span className="bp-stage-strip-label">
          {STAGE_LABEL[effectiveStage]}
        </span>
        <span className="bp-stage-strip-dot">·</span>
        {tools.map((id) => {
          const t = TOOL_REGISTRY[id];
          if (!t) return null;
          return (
            <Link
              key={id}
              to={`${t.to}?from=stage-strip`}
              data-testid={`stage-strip-tool-${id}`}
              className="bp-stage-strip-chip"
            >
              {t.title}
              <ArrowUpRight size={12} />
            </Link>
          );
        })}
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss recommendations"
          data-testid="stage-strip-dismiss"
          className="bp-stage-strip-close"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

export default StageAwareToolStrip;
